import React, { useEffect, useState, useMemo } from "react";
import {
  TextField,
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  useTheme,
  Paper,
  Typography,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "api/userAxios";
import { City, Country } from "types/Country";
import { FilterType } from "types/Filter";
import {
  clientOptions,
  cultureOptions,
  lastSignInOptions,
  ourContactOptions,
  postedVacsOptions,
  serviceRequiredOptions,
  yesNoOptions,
} from "../../../pages/companies-managment/company/companyTable/mockData";

import { CompanyProfileModel } from "model/companyProfileModel";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "components/Loader";
import {
  EyeOutlined,
  SearchOutlined,
  InboxOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  ColumnDef,
  HeaderGroup,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from "@tanstack/react-table";
import IconButton from "components/@extended/IconButton";
import AlertCompanyProfileDelete from "./companyTable/AlertCompanyProfileDelete";
import { MouseEvent } from "react";
import MainCard from "components/MainCard";
import { useCountriesForAutocomplete } from "hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "hooks/query-hooks/useCities";
import { useDebounce } from "hooks";

const SearchCompanies = () => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);

  // Using the same hooks as FiltersModal
  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    selectedCountry?.id || null,
    20,
    debouncedCitySearchTerm
  );

  const [contacted, setContacted] = useState<string>("");
  const [client, setClient] = useState<string>("");
  const [satisfied, setSatisfied] = useState<string>("");
  const [financialAbility, setFinancialAbility] = useState<string>("");
  const [hrDept, setHrDept] = useState<string>("");
  const [trusted, setTrusted] = useState<string>("");
  const [serviceRequired, setServiceRequired] = useState<string>("");
  const [ourContact, setOurContact] = useState<string>("");
  const [culture, setCulture] = useState<string>("");
  const [lastSignIn, setLastSignIn] = useState<string>("");
  const [postedVacs, setPostedVacs] = useState<string>("");
  const [searchResults, setSearchResults] = useState<CompanyProfileModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [customerDeleteId, setCustomerDeleteId] = useState<any>("");

  const handleClose = () => {
    setOpen(!open);
  };

  // Clear city when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.toString()) {
      setSearchText(params.get("title") || "");
      const countryId = params.get("country");
      const cityId = params.get("city");

      // Set country if found in countries list
      if (countryId && countries.length > 0) {
        const country = countries.find((c) => c.id.toString() === countryId);
        if (country) {
          setSelectedCountry(country);
        }
      }

      // Set city if found in cities list
      if (cityId && cities.length > 0) {
        const city = cities.find((c) => c.id.toString() === cityId);
        if (city) {
          setSelectedCity(city);
        }
      }

      setContacted(params.get("contacted") || "");
      setClient(params.get("client") || "");
      setSatisfied(params.get("satisfied") || "");
      setFinancialAbility(params.get("financial_ability") || "");
      setHrDept(params.get("hr_dept") || "");
      setTrusted(params.get("trusted") || "");
      setServiceRequired(params.get("service_required") || "");
      setOurContact(params.get("our_contact") || "");
      setCulture(params.get("culture") || "");
      setLastSignIn(params.get("last_sign_in") || "");
      setPostedVacs(params.get("posted_vacs") || "");
      setHasSearched(true);
      fetchData();
    } else {
      setHasSearched(false);
      setSearchResults([]);
    }
  }, [location.search]);

  const fetchData = () => {
    setLoading(true);
    USERAXIOS.get(`/company_profiles?${location.search}`)
      .then((response) => {
        setSearchResults(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching company profiles:", error);
        setLoading(false);
      });
  };

  const handleSearch = () => {
    setHasSearched(true);
    const filters: FilterType = {
      contacted,
      client,
      satisfied,
      trusted,
      culture,
      financial_ability: financialAbility,
      hr_dept: hrDept,
      service_required: serviceRequired,
      our_contact: ourContact,
      last_sign_in: lastSignIn,
      posted_vacs: postedVacs,
      country: selectedCountry ? selectedCountry.id.toString() : "",
      city: selectedCity ? selectedCity.id.toString() : "",
    };

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.set(key, value);
      }
    });

    if (searchText) {
      queryParams.set("title", searchText);
    }

    navigate({ search: queryParams.toString() });
  };

  const handleClearFilters = () => {
    setSelectedCountry(null);
    setSelectedCity(null);
    setCountrySearchTerm("");
    setCitySearchTerm("");
    setContacted("");
    setClient("");
    setSatisfied("");
    setFinancialAbility("");
    setHrDept("");
    setTrusted("");
    setServiceRequired("");
    setOurContact("");
    setCulture("");
    setLastSignIn("");
    setPostedVacs("");
    setSearchResults([]);
    setSearchText("");
    setHasSearched(false);
    navigate({ search: "" });
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo<ColumnDef<CompanyProfileModel>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
      },
      {
        header: () => <FormattedMessage id="Company Name" />,
        accessorKey: "name_en",
      },
      {
        header: () => <FormattedMessage id="User Name" />,
        accessorKey: "user_name",
      },
      {
        header: () => <FormattedMessage id="Location" />,
        accessorKey: "location",
        cell: ({ row }) => `${row.original.country}, ${row.original.city}`,
      },
      {
        header: () => <FormattedMessage id="Registration Date" />,
        accessorKey: "registration_date",
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row }) => {
          const { id } = row.original;
          const handleEditProfile = () => {
            window.location.href = `companies-list/edit-company/${id}`;
          };
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="View Details">
                <IconButton
                  color="primary"
                  href={`/companies-list/view-company-details/${id}`}
                  target="_blank"
                >
                  <EyeOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Profile">
                <IconButton color="primary" onClick={handleEditProfile}>
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setCustomerDeleteId(row.original.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme]
  );

  const table = useReactTable({
    data: searchResults,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <MainCard title={<FormattedMessage id="Search Companies" />}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by company title..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            value={selectedCountry}
            onChange={(event, newValue) => {
              setSelectedCountry(newValue);
              setSelectedCity(null);
              // Clear search term when option is selected
              if (newValue) {
                setCountrySearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setCountrySearchTerm(newInputValue);
              }
            }}
            options={countries}
            getOptionLabel={(option: Country) =>
              theme.direction === "rtl" ? option.name_trans.ar : option.name
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="Country" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCountries && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreCountries &&
                  !loadingCountries
                ) {
                  loadMoreCountries();
                }
              },
            }}
            loading={loadingCountries}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedCountrySearchTerm.length >= 2
                ? "No countries found"
                : "Type to search countries"
            }
          />
          {countriesError && (
            <FormHelperText error>
              Error loading countries. Please try again.
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            value={selectedCity}
            onChange={(event, newValue) => {
              setSelectedCity(newValue);
              // Clear search term when option is selected
              if (newValue) {
                setCitySearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setCitySearchTerm(newInputValue);
              }
            }}
            options={cities}
            getOptionLabel={(option: City) =>
              theme.direction === "rtl" ? option.name_trans.ar : option.name
            }
            disabled={!selectedCountry}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="City" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCities && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreCities &&
                  !loadingCities
                ) {
                  loadMoreCities();
                }
              },
            }}
            loading={loadingCities}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedCitySearchTerm.length >= 2
                ? "No cities found"
                : "Type to search cities"
            }
          />
          {citiesError && (
            <FormHelperText error>
              Error loading cities. Please try again.
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Contacted" />
            </InputLabel>
            <Select
              value={contacted}
              onChange={(e) => setContacted(e.target.value)}
              label={<FormattedMessage id="Contacted" />}
            >
              {yesNoOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Client" />
            </InputLabel>
            <Select
              value={client}
              onChange={(e) => setClient(e.target.value)}
              label={<FormattedMessage id="Client" />}
            >
              {clientOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Satisfied" />
            </InputLabel>
            <Select
              value={satisfied}
              onChange={(e) => setSatisfied(e.target.value)}
              label={<FormattedMessage id="Satisfied" />}
            >
              {yesNoOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Financial Ability" />
            </InputLabel>
            <Select
              value={financialAbility}
              onChange={(e) => setFinancialAbility(e.target.value)}
              label={<FormattedMessage id="Financial Ability" />}
            >
              {yesNoOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="HR Department" />
            </InputLabel>
            <Select
              value={hrDept}
              onChange={(e) => setHrDept(e.target.value)}
              label={<FormattedMessage id="HR Department" />}
            >
              {yesNoOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Trusted" />
            </InputLabel>
            <Select
              value={trusted}
              onChange={(e) => setTrusted(e.target.value)}
              label={<FormattedMessage id="Trusted" />}
            >
              {yesNoOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Service Required" />
            </InputLabel>
            <Select
              value={serviceRequired}
              onChange={(e) => setServiceRequired(e.target.value)}
              label={<FormattedMessage id="Service Required" />}
            >
              {serviceRequiredOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Our Contact" />
            </InputLabel>
            <Select
              value={ourContact}
              onChange={(e) => setOurContact(e.target.value)}
              label={<FormattedMessage id="Our Contact" />}
            >
              {ourContactOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Culture" />
            </InputLabel>
            <Select
              value={culture}
              onChange={(e) => setCulture(e.target.value)}
              label={<FormattedMessage id="Culture" />}
            >
              {cultureOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}{" "}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Last Sign In" />
            </InputLabel>
            <Select
              value={lastSignIn}
              onChange={(e) => setLastSignIn(e.target.value)}
              label={<FormattedMessage id="Last Sign In" />}
            >
              {lastSignInOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Posted Vacancies" />
            </InputLabel>
            <Select
              value={postedVacs}
              onChange={(e) => setPostedVacs(e.target.value)}
              label={<FormattedMessage id="Posted Vacancies" />}
            >
              {postedVacsOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClearFilters}
          sx={{ width: "50%" }}
        >
          <FormattedMessage id="Clear Filters" />
        </Button>
        <Button
          onClick={handleSearch}
          variant="contained"
          color="primary"
          sx={{ width: "50%" }}
        >
          <FormattedMessage id="Search" />
        </Button>
      </Box>

      {loading ? (
        <Loader />
      ) : hasSearched ? (
        searchResults.length > 0 ? (
          <TableContainer sx={{ mt: 5 }}>
            <Table>
              <TableHead>
                {table
                  .getHeaderGroups()
                  .map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Stack
            sx={{ mt: 5, textAlign: "center" }}
            spacing={1}
            alignItems="center"
          >
            <InboxOutlined
              style={{ fontSize: "5rem", color: theme.palette.grey[400] }}
            />
            <Typography variant="h6">
              <FormattedMessage id="No available data" />
            </Typography>
          </Stack>
        )
      ) : null}

      <AlertCompanyProfileDelete
        setResult={setSearchResults}
        id={customerDeleteId}
        title={customerDeleteId}
        open={open}
        handleClose={handleClose}
      />
    </MainCard>
  );
};

export default SearchCompanies;
